import { createClient } from '@supabase/supabase-js';
import type { Meeting, MeetingNote, Participant, SoundstagePreferences, MeetingAnalytics, ConflictCheckResult } from '@/types/soundstage';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:54321';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

export async function callSoundstageFunction(action: string, payload: any = {}) {
    const { data, error } = await supabase.functions.invoke('soundstage-management', {
        body: {
            action,
            ...payload,
        },
    });

    if (error) {
        // Supabase SDK wraps non-2xx as FunctionsHttpError — try to extract the JSON body
        let detail = error.message || 'SoundStage operation failed';
        try {
            const ctx = (error as any).context;
            if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                if (body?.error) detail = body.error;
            }
        } catch {}
        throw new Error(detail);
    }

    if (!data || (data.error && !data.ok)) {
        throw new Error(data?.error || 'SoundStage operation failed');
    }

    return data;
}

export async function createMeeting(
    organizationId: string,
    clerkUserId: string,
    title: string,
    description?: string,
    scheduledStartAt?: string,
    scheduledEndAt?: string,
    roomMode?: 'normal' | 'group',
    recurrenceRule?: 'none' | 'daily' | 'weekly',
    recurrenceEndDate?: string,
    guestAccessEnabled?: boolean,
): Promise<Meeting> {
    const result = await callSoundstageFunction('create_meeting', {
        organizationId,
        clerkUserId,
        title,
        description,
        scheduledStartAt,
        scheduledEndAt,
        roomMode,
        recurrenceRule,
        recurrenceEndDate,
        guestAccessEnabled,
    });
    return result.meeting;
}

export async function getMeetings(
    organizationId: string,
    clerkUserId: string,
    status?: string,
    limit?: number,
    offset?: number,
    dateFrom?: string,
    dateTo?: string,
    viewUserProfileId?: string,
): Promise<Meeting[]> {
    const result = await callSoundstageFunction('get_meetings', {
        organizationId,
        clerkUserId,
        status,
        limit,
        offset,
        dateFrom,
        dateTo,
        viewUserProfileId,
    });
    return result.meetings;
}

export async function getMeeting(
    meetingId: string,
    clerkUserId: string,
): Promise<Meeting> {
    const result = await callSoundstageFunction('get_meeting', {
        meetingId,
        clerkUserId,
    });
    return result.meeting;
}

export async function updateMeeting(
    meetingId: string,
    clerkUserId: string,
    updates: { title?: string; description?: string; scheduledStartAt?: string; scheduledEndAt?: string },
): Promise<Meeting> {
    const result = await callSoundstageFunction('update_meeting', {
        meetingId,
        clerkUserId,
        ...updates,
    });
    return result.meeting;
}

export async function cancelMeeting(
    meetingId: string,
    clerkUserId: string,
): Promise<void> {
    await callSoundstageFunction('cancel_meeting', { meetingId, clerkUserId });
}

export async function endMeeting(
    meetingId: string,
    clerkUserId: string,
): Promise<{ duration_minutes: number | null }> {
    return await callSoundstageFunction('end_meeting', { meetingId, clerkUserId });
}

export async function joinMeeting(
    meetingId: string,
    clerkUserId: string,
): Promise<{ roomUrl: string; isHost: boolean; meetingId: string }> {
    const result = await callSoundstageFunction('join_meeting', { meetingId, clerkUserId });
    return { roomUrl: result.roomUrl, isHost: result.isHost, meetingId: result.meetingId || meetingId };
}

export async function leaveMeeting(
    meetingId: string,
    clerkUserId: string,
): Promise<void> {
    await callSoundstageFunction('leave_meeting', { meetingId, clerkUserId });
}

export async function inviteParticipants(
    meetingId: string,
    clerkUserId: string,
    participantIds: string[],
): Promise<void> {
    await callSoundstageFunction('invite_participants', {
        meetingId,
        clerkUserId,
        participantIds,
    });
}

export async function removeParticipant(
    meetingId: string,
    clerkUserId: string,
    participantUserId: string,
): Promise<void> {
    await callSoundstageFunction('remove_participant', {
        meetingId,
        clerkUserId,
        participantId: participantUserId,
    });
}

export async function getParticipants(
    meetingId: string,
    clerkUserId: string,
): Promise<Participant[]> {
    const result = await callSoundstageFunction('get_participants', {
        meetingId,
        clerkUserId,
    });
    return result.participants;
}

export async function createNote(
    meetingId: string,
    clerkUserId: string,
    noteContent: string,
    noteType: 'note' | 'action_item' | 'decision' = 'note',
): Promise<MeetingNote> {
    const result = await callSoundstageFunction('create_note', {
        meetingId,
        clerkUserId,
        noteContent,
        noteType,
    });
    return result.note;
}

export async function getNotes(
    meetingId: string,
    clerkUserId: string,
): Promise<MeetingNote[]> {
    const result = await callSoundstageFunction('get_notes', {
        meetingId,
        clerkUserId,
    });
    return result.notes;
}

export async function updateNote(
    noteId: string,
    clerkUserId: string,
    updates: { noteContent?: string; isCompleted?: boolean },
): Promise<MeetingNote> {
    const result = await callSoundstageFunction('update_note', {
        noteId,
        clerkUserId,
        ...updates,
    });
    return result.note;
}

export async function getUserPreferences(
    clerkUserId: string,
): Promise<SoundstagePreferences | null> {
    const result = await callSoundstageFunction('get_user_preferences', { clerkUserId });
    return result.preferences || null;
}

export async function saveUserPreferences(
    clerkUserId: string,
    preferences: SoundstagePreferences,
): Promise<void> {
    await callSoundstageFunction('save_user_preferences', { clerkUserId, preferences });
}

export async function getMeetingAnalytics(
    organizationId: string,
    clerkUserId: string,
): Promise<MeetingAnalytics> {
    const result = await callSoundstageFunction('get_meeting_analytics', {
        organizationId,
        clerkUserId,
    });
    return result.analytics;
}

// ==================== CONFLICT CHECKING ====================

export async function checkConflicts(
    organizationId: string,
    clerkUserId: string,
    participantIds: string[],
    scheduledStartAt: string,
    scheduledEndAt: string,
): Promise<ConflictCheckResult> {
    const result = await callSoundstageFunction('check_conflicts', {
        organizationId,
        clerkUserId,
        participantIds,
        scheduledStartAt,
        scheduledEndAt,
    });
    return {
        has_conflicts: result.has_conflicts,
        participant_conflicts: result.participant_conflicts,
        alternative_slots: result.alternative_slots,
    };
}

// ==================== GUEST ACCESS ====================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:54321';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

async function callGuestFunction(action: string, payload: Record<string, any>) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/soundstage-management`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ action, ...payload }),
    });

    const data = await res.json();
    if (!res.ok || data.error) {
        throw new Error(data.error || 'Guest operation failed');
    }
    return data;
}

export async function getMeetingAsGuest(
    meetingId: string,
    guestToken: string,
): Promise<{ id: string; title: string; status: string; scheduled_start_at?: string; participant_count: number; max_participants: number }> {
    const result = await callGuestFunction('get_meeting_guest', { meetingId, guestToken });
    return result.meeting;
}

export async function joinMeetingAsGuest(
    meetingId: string,
    guestName: string,
    guestToken: string,
): Promise<{ guestId: string; meetingId: string; pendingApproval: boolean; roomUrl?: string }> {
    const result = await callGuestFunction('join_meeting_as_guest', { meetingId, guestName, guestToken });
    return {
        guestId: result.guestId,
        meetingId: result.meetingId,
        pendingApproval: !!result.pendingApproval,
        roomUrl: result.roomUrl,
    };
}

export async function checkGuestStatus(
    meetingId: string,
    guestId: string,
): Promise<{ status: 'pending' | 'approved' | 'denied' | string; roomUrl?: string }> {
    const result = await callGuestFunction('check_guest_status', { meetingId, guestId });
    return { status: result.status, roomUrl: result.roomUrl };
}

export async function leaveMeetingAsGuest(
    meetingId: string,
    guestId: string,
): Promise<void> {
    await callGuestFunction('leave_meeting_as_guest', { meetingId, guestId });
}

export async function toggleGuestAccess(
    meetingId: string,
    clerkUserId: string,
    enabled: boolean,
): Promise<{ guest_access_enabled: boolean; guest_access_token: string | null }> {
    const result = await callSoundstageFunction('toggle_guest_access', {
        meetingId,
        clerkUserId,
        guestAccessEnabled: enabled,
    });
    return { guest_access_enabled: result.guest_access_enabled, guest_access_token: result.guest_access_token };
}
